import React from 'react';
import {Button} from '~/components/Button';
import {CheckboxInput} from '~/components/Controls/Checkbox';
import {TagsList} from '~/components/Controls/Tags';
import {InfoPopover} from '~/components/InfoPopover';
import {IColumn, Table} from '~/components/Table';
import {
    AVAILABLE_FORMATS,
    CREATIVE_FORMATS,
    creativeFormatLabels,
    creativeFormats,
    CreativeFormat,
    PlacementFormatsTable,
    SupplyPlacement,
    supplyPlacementLabels,
    supplyPlacements
} from '~/modules/Bidders/models/Bidder';
import style from './PlacementFormatsBlock.module.scss';

export const NOT_CONFIGURED_TEXT = 'Not configured, legacy rules apply';

const ROW_NOTES: Record<SupplyPlacement, string> = {
    fullscreen: 'interstitial, not rewarded',
    rewarded: 'interstitial, rewarded',
    small_banner: 'not interstitial, any size but 300×250',
    mrec: 'not interstitial, 300×250',
    native: 'native placement'
};

const COLUMN_NOTES: Partial<Record<CreativeFormat, string>> = {
    [CREATIVE_FORMATS.video]: 'MREC: rendered as in-banner video, BidMachine SDK only',
    [CREATIVE_FORMATS.native]: 'Fullscreen, Rewarded, Small banner, MREC: rendered as in-banner native. Native row: native creative as is'
};

const ticked = (table: PlacementFormatsTable | null, placement: SupplyPlacement): CreativeFormat[] =>
    creativeFormats.filter(format => !!table?.[placement]?.includes(format));

const MULTI_FORMAT_HINT = 'Off: one bid request per ticked format, sent in parallel. On: one bid request whose imp carries every ticked format.';

export interface IPlacementFormatsBlockProps {
    editing: boolean;
    disabled?: boolean;
    table: PlacementFormatsTable | null | undefined;
    multiFormatRequest: boolean;
    onChangeTable?: (next: PlacementFormatsTable | null) => void;
    onChangeMultiFormatRequest?: (next: boolean) => void;
    actions?: React.ReactNode;
    notes?: string[];
    /** The form's validation error for the table, shown under it on submit. */
    error?: string;
}

interface Row {
    placement: SupplyPlacement;
}

export const PlacementFormatsBlock = (props: IPlacementFormatsBlockProps) => {
    const {editing, disabled, multiFormatRequest, onChangeTable, onChangeMultiFormatRequest, actions, notes = [], error} = props;
    const table = props.table ?? null;

    const toggle = (placement: SupplyPlacement, format: CreativeFormat, checked: boolean) => {
        const current = table?.[placement] ?? [];
        const formats = checked ? [...current, format] : current.filter(item => item !== format);
        onChangeTable?.({...table, [placement]: creativeFormats.filter(item => formats.includes(item))});
    };

    const multiFormat = <div className={style.multi}>
        {editing
            ? <CheckboxInput
                label='Multi-format request'
                value={multiFormatRequest}
                disabled={disabled}
                onChange={value => onChangeMultiFormatRequest?.(value)}
            />
            : <span>Multi-format request: {multiFormatRequest ? 'on' : 'off'}</span>}
        <InfoPopover content={MULTI_FORMAT_HINT} ariaLabel={MULTI_FORMAT_HINT} width={280}/>
    </div>;

    if (table === null) {
        return <div className={style.block}>
            <span>{NOT_CONFIGURED_TEXT}</span>
            {editing
                ? <div className={style.footer}>
                    {multiFormat}
                    <div className={style.buttons}>
                        <Button disabled={disabled} onClick={() => onChangeTable?.({})}>Configure table</Button>
                        {actions}
                    </div>
                </div>
                : multiFormat}
            {notes.length > 0 && <ul className={style.notes}>{notes.map((note, index) => <li key={index}>{note}</li>)}</ul>}
        </div>;
    }

    if (!editing) {
        const configured = supplyPlacements.filter(placement => ticked(table, placement).length > 0);
        return <div className={style.block}>
            {configured.length
                ? <div className={style.list}>
                    {configured.map(placement => <div key={placement} className={style.listRow}>
                        <span className={style.listLabel}>{supplyPlacementLabels[placement]}</span>
                        <TagsList values={ticked(table, placement).map(format => creativeFormatLabels[format].toLowerCase())} size='small'/>
                    </div>)}
                </div>
                : <span className={style.cellOff}>No format selected on any placement</span>}
            {multiFormat}
        </div>;
    }

    const rows: Row[] = supplyPlacements.map(placement => ({placement}));

    const formatColumn = (format: CreativeFormat): IColumn<Row> => {
        const note = COLUMN_NOTES[format];
        return {
            header: <span className={style.cell}>
                {creativeFormatLabels[format]}
                {note && <InfoPopover className={style.headerInfo} content={note} ariaLabel={note} width={240}/>}
            </span>,
            cellAttrs: {className: style.formatCell},
            cell: ({placement}) => AVAILABLE_FORMATS[placement].includes(format)
                ? <CheckboxInput
                    value={ticked(table, placement).includes(format)}
                    disabled={disabled}
                    onChange={value => toggle(placement, format, value)}
                    aria-label={`${supplyPlacementLabels[placement]}: ${creativeFormatLabels[format]}`}
                />
                : <span className={style.cellOff}>—</span>
        };
    };

    const columns: IColumn<Row>[] = [
        {
            header: 'Supply placement',
            small: true,
            cell: ({placement}) => <span className={style.rowName}>
                {supplyPlacementLabels[placement]}
                <InfoPopover content={ROW_NOTES[placement]} ariaLabel={ROW_NOTES[placement]} width={240}/>
            </span>
        },
        ...creativeFormats.map(formatColumn)
    ];

    return <div className={style.block}>
        <Table<Row>
            header
            records={rows}
            columns={columns}
        />
        {rows.every(({placement}) => !ticked(table, placement).length) && <span className={style.hidden}>
            No format ticked. Tick one, or clear the table to keep the bidder on legacy rules.
        </span>}
        {error && <span className={style.error}>{error}</span>}
        <div className={style.footer}>
            {multiFormat}
            <div className={style.buttons}>
                <Button disabled={disabled} onClick={() => { onChangeTable?.(null); onChangeMultiFormatRequest?.(false); }}>Clear table (legacy rules)</Button>
                {actions}
            </div>
        </div>
        {notes.length > 0 && <ul className={style.notes}>{notes.map((note, index) => <li key={index}>{note}</li>)}</ul>}
    </div>;
};
